import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { FormikErrors, FormikValues } from 'formik';
import { useNavigate } from 'react-router-dom';
import { api } from '@/modules/common/api/index.ts';
import { queryClient } from '@/modules/common/query.ts';
import { withErrorHandling } from '@/modules/common/utils/error-utils.tsx';
import { entityPaths } from '@/modules/entity/routes.tsx';
import { EntityItemFullResponse } from '@/modules/entity/types/EntityItemResponse.ts';
import { validateABNorACN } from '@/modules/entity/utils/abn-acn.ts';
import {
  BronIDFollowUpRequest,
  BronIDLookupResponse,
  CreateEntityRequest,
  CreateEntityResponse,
  DeleteEntityDocumentV1UsersEntitiesDocumentsDeleteParams,
  EntityStatus,
  FindShareholdersByRegisterV1SrShareholdersRegisterGetParams,
  GetBronIdLookupV1SrBronIdLookupGetParams,
  GetCurrentUserEntitiesV1UsersCurrentEntitiesGetParams,
  GetEntityItemResponse,
  GetRegisterByPrivateIdResponse,
  GetShareholdersByRegisterIdResponse,
  GetUserEntitiesResponse,
  UpdateEntityResponse,
} from 'api';

export const USER_ENTITIES_QUERY_KEY = 'vestra/user-entities';
export const CURRENT_ENTITY_QUERY_KEY = 'vestra/current-entity';
export const CURRENT_ENTITY_CREATING_KEY = 'vestra/creating-entity';
export const CURRENT_REGISTER_APPLY_ID_QUERY_KEY = 'vestra/createng-entity-register-id';
export const CURRENT_REGISTER_CLASS_APPLY_ID_QUERY_KEY = 'vestra/creating-entity-register-class-id';
export const FOLLOW_UP_ENTITY_ID_QUERY_KEY = 'vestra/follow-up-id';
export const SHAREHOLDERS_BY_REGISTER_ID = 'vestra/shareholders-by-entity-id-and-register-id';
export const REGISTER_BY_PRIVATE_ID_QUERY_KEY = 'vestra/register-by-private-id';

export const useCreateEntityMutation = ({
  onSuccess,
}: {
  onSuccess?: (data: CreateEntityResponse) => void;
}) => {
  return useMutation({
    mutationFn: async (payload: CreateEntityRequest) => {
      const response = await withErrorHandling(api.v1.createEntityV1UsersEntitiesPost(payload), {
        loading: 'Creating entity...',
        success: 'Entity draft created, please proceed to add more details and finalise KYC.',
        notificationDuration: 10000,
      });
      return response.data;
    },
    onSuccess: (data: CreateEntityResponse) => {
      queryClient.setQueryData<CreateEntityResponse>([CURRENT_ENTITY_CREATING_KEY], data);
      queryClient.invalidateQueries([USER_ENTITIES_QUERY_KEY]);
      onSuccess && onSuccess(data);
    },
  });
};

export const useUpdateEntityMutation = ({
  setErrors,
  onSuccess,
}: {
  setErrors?: (errors: FormikErrors<FormikValues>) => void;
  onSuccess?: (data: UpdateEntityResponse) => void;
}) => {
  return useMutation({
    mutationFn: async (payload: any) => {
      const response = await withErrorHandling(api.v1.updateEntityV1UsersEntitiesPatch(payload), {
        loading: 'Updating entity...',
        success: 'Entity updated',
        setErrors,
      });
      return response.data;
    },
    onSuccess: (data: UpdateEntityResponse) => {
      if (onSuccess) onSuccess(data);
      queryClient.setQueryData<UpdateEntityResponse>([CURRENT_ENTITY_CREATING_KEY], data);
      const current = queryClient.getQueryData<EntityItemFullResponse>([
        CURRENT_ENTITY_QUERY_KEY,
        data.entity.id,
      ]);
      queryClient.setQueryData<EntityItemFullResponse>([CURRENT_ENTITY_QUERY_KEY, data.entity.id], {
        entity: {
          ...(current?.entity ?? {}),
          entity_id: data.entity.entity_id,
        },
      } as EntityItemFullResponse);
      queryClient.invalidateQueries([CURRENT_ENTITY_QUERY_KEY]);
      queryClient.invalidateQueries([USER_ENTITIES_QUERY_KEY]);
    },
  });
};

export const useDeleteEntityDocumentMutation = ({ onSuccess }: { onSuccess?: () => void } = {}) => {
  return useMutation({
    mutationFn: async (payload: DeleteEntityDocumentV1UsersEntitiesDocumentsDeleteParams) => {
      const response = await withErrorHandling(
        api.v1.deleteEntityDocumentV1UsersEntitiesDocumentsDelete(payload),
        {
          loading: 'Document deletion...',
          success: 'Document deleted',
        },
      );
      return response.data;
    },
    onSuccess: async () => {
      // const data = queryClient.getQueryData<EntityItemAnyResponse>([CURRENT_ENTITY_CREATING_KEY]);
      await queryClient.invalidateQueries([CURRENT_ENTITY_QUERY_KEY]);
      onSuccess && onSuccess();
    },
    // onSuccess: (data: DeleteEntityDocumentResponse) => {
    // queryClient.setQueryData<UpdateEntityResponse>([CURRENT_ENTITY_CREATING_KEY], data);
    // queryClient.invalidateQueries([CURRENT_ENTITY_QUERY_KEY]);
    // },
  });
};

export const useGetBronIdLookUpByAPNQuery = ({
  onSuccess,
}: { onSuccess?: (data: BronIDLookupResponse) => Promise<void> } = {}) => {
  return useMutation({
    mutationFn: async (payload: GetBronIdLookupV1SrBronIdLookupGetParams) => {
      const { abn } = payload;
      const type = validateABNorACN(abn);
      const response = await withErrorHandling(api.v1.getBronIdLookupV1SrBronIdLookupGet(payload), {
        loading: `Looking up info by ${type === 'invalid' ? 'ABN / ACN' : type}...`,
        success: `Found info by ${type === 'invalid' ? 'ABN / ACN' : type}`,
      });
      return response.data;
    },
    onSuccess: async (data: BronIDLookupResponse) => {
      onSuccess && (await onSuccess(data));
    },
  });
};

export const useCurrentUserEntitiesQuery = (
  payload: GetCurrentUserEntitiesV1UsersCurrentEntitiesGetParams & {
    invalidateQueriesCurrentEntity?: boolean;
  },
) => {
  const { invalidateQueriesCurrentEntity, ...data } = payload;

  return useInfiniteQuery<GetUserEntitiesResponse>({
    queryKey: [USER_ENTITIES_QUERY_KEY, data.page_size],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async ({ pageParam = 0 }) => {
      const response = await api.v1.getCurrentUserEntitiesV1UsersCurrentEntitiesGet({
        ...data,
        page: pageParam || data.page,
      });
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
    refetchInterval: data => {
      const result = data?.pages.some(newPage =>
        newPage.data.some(newItem => {
          return (
            newItem.status === EntityStatus.EntityAwaitingApproval ||
            newItem.status === EntityStatus.EntityUnsuccessfull
          );
        }),
      );
      if (result && invalidateQueriesCurrentEntity) {
        queryClient.invalidateQueries([CURRENT_ENTITY_QUERY_KEY]);
      }
      return result ? 30000 : false;
    },
  });
};

export const useEntityItemQuery = (entityId: number, disabled = false) => {
  return useQuery<GetEntityItemResponse>({
    queryKey: [CURRENT_ENTITY_QUERY_KEY], //don't add any params
    // staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getEntityV1UsersEntitiesEntityIdGet({ entityId });
      return response.data;
    },
    enabled: !disabled,
  });
};

interface UseEntityItemUncachedQueryOptions {
  refetchInterval?: number | false;
  refetchIntervalInBackground?: boolean;
}

export const useEntityItemUncachedQuery = (
  entityId: number,
  disabled = false,
  options: UseEntityItemUncachedQueryOptions = {},
) => {
  return useQuery<EntityItemFullResponse>({
    queryKey: [CURRENT_ENTITY_QUERY_KEY, entityId],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    refetchInterval: options.refetchInterval,
    refetchIntervalInBackground: options.refetchIntervalInBackground,
    queryFn: async () => {
      const response = await api.v1.getEntityV1UsersEntitiesEntityIdGet({ entityId });
      return response.data as EntityItemFullResponse;
    },
    enabled: !disabled,
  });
};

export const useRegisterByPrivateIdQuery = (id: string, disabled = false) => {
  return useQuery<GetRegisterByPrivateIdResponse>({
    queryKey: [REGISTER_BY_PRIVATE_ID_QUERY_KEY, id],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getRegisterByPrivateIdV1SrRegisterInviteIdGet({ id });
      return response.data;
    },
    enabled: !disabled,
  });
};

export const useSetEntityAwaitingApprovalMutation = ({
  onSuccess,
}: { onSuccess?: () => void } = {}) => {
  return useMutation({
    mutationFn: async (entityId: number) => {
      const response = await withErrorHandling(
        api.v1.entitySetAwaitingApprovedV1UsersEntitiesStatusAwaitingApprovedPatch({
          id: entityId,
        }),
        {
          notificationDuration: 10000,
          loading: 'Submitting your information for verification...',
          success:
            'Information has been submitted for verification, please wait for email letters with an update on your status.',
        },
      );
      return response.data;
    },
    onSuccess: () => {
      // const apply_register_class_id = queryClient.getQueryData([
      //   CURRENT_REGISTER_CLASS_APPLY_ID_QUERY_KEY,
      // ]);
      // const apply_register_id = queryClient.getQueryData([CURRENT_REGISTER_APPLY_ID_QUERY_KEY]);
      // queryClient.setQueryData([CURRENT_ENTITY_QUERY_KEY], { entity });
      queryClient.invalidateQueries([CURRENT_ENTITY_QUERY_KEY]);
      queryClient.invalidateQueries([USER_ENTITIES_QUERY_KEY]);

      onSuccess && onSuccess();
    },
  });
};

export const useEntityFollowUpMutation = ({
  setErrors,
}: {
  setErrors: (errors: FormikErrors<FormikValues>) => void;
}) => {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (payload: BronIDFollowUpRequest) => {
      const response = await withErrorHandling(
        api.v1.bronidFollowUpV1SrEntityBronidFollowUpPost(payload),
        {
          notificationDuration: 10000,
          loading: 'Submitting your information for verification...',
          success:
            'Information has been submitted for verification, please wait for email letters with an update on your status.',
          setErrors,
        },
      );
      return response.data;
    },
    onSuccess: async () => {
      queryClient.removeQueries([FOLLOW_UP_ENTITY_ID_QUERY_KEY]);
      await queryClient.invalidateQueries({ queryKey: [CURRENT_ENTITY_QUERY_KEY] });
      await queryClient.invalidateQueries({ queryKey: [USER_ENTITIES_QUERY_KEY] });
      navigate(entityPaths.static.entityList);
    },
  });
};

export const useGetShareholdersByEntityIdAndRegisterId = (
  payload: FindShareholdersByRegisterV1SrShareholdersRegisterGetParams,
  registerId: number,
  disabled = false,
) => {
  return useQuery<GetShareholdersByRegisterIdResponse>({
    queryKey: [SHAREHOLDERS_BY_REGISTER_ID, JSON.stringify(payload), registerId],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.findShareholdersByRegisterV1SrShareholdersRegisterGet(payload, {
        headers: { 'xRegister-ID': registerId },
      });
      return response.data;
    },
    enabled: !disabled,
  });
};
